import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '@config/firebase';
import { useSurvey } from '@contexts/SurveyContext';
import { useNavigate } from 'react-router-dom';
import { logError, logInfo } from '@utils/logger';
import { useState } from 'react';

const useSurveyActions = ({ setToast, preventRedirect = false, onLoadComplete = null }) => {
  const navigate = useNavigate();
  const { startSurvey, endSurvey } = useSurvey();
  const [showEditSurvey, setShowEditSurvey] = useState(false);
  const [surveyToEdit, setSurveyToEdit] = useState(null);

  const handleSurveyEdit = (survey) => {
    setShowEditSurvey(true);
    setSurveyToEdit(survey);
  };

  const handleSurveyUpdate = async (updatedData) => {
    try {
      if (!surveyToEdit?.id) {
        throw new Error('Invalid survey data');
      }

      const surveyRef = doc(db, 'surveys', surveyToEdit.id);
      await updateDoc(surveyRef, {
        ...updatedData,
        updatedAt: new Date().toISOString()
      });
      
      setShowEditSurvey(false);
      setSurveyToEdit(null);
      setToast({
        type: 'success',
        message: 'Survey updated successfully'
      });
      logInfo('Survey updated successfully');
     
     // Trigger a refetch to update the survey list
     if (typeof refetch === 'function') {
       setTimeout(() => refetch(), 500);
     }
    } catch (error) {
      logError('Failed to update survey:', error);
      setToast({
        type: 'error',
        message: 'Failed to update survey'
      });
    }
  };

  const handleLoadSurvey = async (surveyId) => {
    try {
      if (!surveyId) {
        localStorage.removeItem('activeSurveyId');
        throw new Error('Invalid survey ID');
      }

      if (!auth.currentUser) {
        localStorage.removeItem('activeSurveyId');
        throw new Error('You must be logged in to load a survey');
      }

      const surveyRef = doc(db, 'surveys', surveyId);
      const surveyDoc = await getDoc(surveyRef).catch(error => {
        localStorage.removeItem('activeSurveyId');
        logError('Failed to fetch survey:', error);
        throw new Error('Failed to load survey data');
      });
      
      if (!surveyDoc.exists()) {
        localStorage.removeItem('activeSurveyId');
        throw new Error('Survey not found');
      }

      const surveyData = { id: surveyId, ...surveyDoc.data() };

      // Check if this is the currently active survey (unload case)
      const isUnloading = surveyData.id === localStorage.getItem('activeSurveyId');
      
      if (isUnloading) {
        // Unload the survey
        await updateDoc(surveyRef, {
          status: 'inactive',
          updatedAt: new Date().toISOString()
        });
        
        await endSurvey();
        localStorage.removeItem('activeSurveyId');

        // Dispatch custom event for local updates
        window.dispatchEvent(new CustomEvent('localStorageUpdate', {
          detail: { key: 'activeSurveyId', newValue: null }
        }));
        
        setToast({
          type: 'success',
          message: 'Survey unloaded successfully'
        });
        
        logInfo('Survey unloaded successfully');
        return true;
      }

      // Loading a new survey
      // Validate survey ownership
      const isAdmin = auth.currentUser?.email === 'jfprince@soltec.ca' || 
                     auth.currentUser?.email === 'jfprince29@gmail.com' ||
                     auth.currentUser?.email === 'admin@oversizehub.net';
      
      const canAccess = isAdmin ||
                       surveyData.ownerId === auth.currentUser?.uid ||
                       surveyData.sharedWith?.includes(auth.currentUser?.uid) ||
                       surveyData.isShared;
                       
      if (!canAccess) {
        localStorage.removeItem('activeSurveyId');
        throw new Error('You do not have access to this survey');
      }

      // Update survey status to active
      await updateDoc(surveyRef, {
        status: 'active',
        updatedAt: new Date().toISOString()
      });

      // Store active survey ID
      localStorage.setItem('activeSurveyId', surveyId);
      
      // Dispatch custom event for local updates
      window.dispatchEvent(new CustomEvent('localStorageUpdate', {
        detail: { key: 'activeSurveyId', newValue: surveyId }
      }));

      await startSurvey(surveyData);
      logInfo('Survey loaded successfully');
      
      // Call onLoadComplete callback if provided
      onLoadComplete?.();
      
      setToast({
        type: 'success',
        message: 'Survey loaded successfully'
      });
      
      return true;
    } catch (error) {
      // Ensure activeSurveyId is removed from localStorage on any error
      localStorage.removeItem('activeSurveyId');
      
      // Dispatch custom event for local updates
      window.dispatchEvent(new CustomEvent('localStorageUpdate', {
        detail: { key: 'activeSurveyId', newValue: null }
      }));
      
      setToast({
        type: 'error',
        message: error.message || 'Failed to load survey. Please try again.'
      });
      logError('Failed to load survey:', error);
      return false;
    }
  };

  const handleViewMap = () => {
    const path = preventRedirect ? '../map' : 'map';
    navigate(path, {
      state: { source: 'roadscope' }
    });
  };

  return {
    handleSurveyEdit,
    handleSurveyUpdate,
    handleLoadSurvey,
    handleViewMap,
    showEditSurvey,
    setShowEditSurvey,
    surveyToEdit,
    setSurveyToEdit
  };
};

export default useSurveyActions;

export { useSurveyActions }